/**
 * sceneAssets - where the scene files (splats, backdrops, walk grids, overlay
 * JSON) are served from.
 *
 * The registry authors every path as a same-origin `/scenes/...` URL. Setting
 * `REACT_APP_SCENE_BASE_URL` at build time (CRA inlines it) moves them all to
 * another origin or prefix, e.g. `https://cdn.example.com/scenes`: the leading
 * `/scenes` of each registry path is replaced by that base, with or without a
 * trailing slash. Unset or empty, every path stays exactly as authored.
 *
 * Overlay JSON and mesh-room JSON (`meshRoom`) resolve their own image and
 * mesh paths relative to their URL, so rewriting the JSON URL is enough to
 * move their files with it.
 */

export const SCENE_PATH_PREFIX = '/scenes';

/**
 * Trim a configured base and drop trailing slashes; '' when unset.
 *
 * @param {unknown} base
 * @returns {string}
 */
export const normalizeSceneBase = (base) => (
  typeof base === 'string' ? base.trim().replace(/\/+$/, '') : ''
);

export const SCENE_BASE_URL = normalizeSceneBase(process.env.REACT_APP_SCENE_BASE_URL);

/**
 * Rewrite one registry path onto `base`. Paths outside `/scenes` (and every
 * path when `base` is empty) come back unchanged.
 *
 * @param {string} path
 * @param {string} [base]
 * @returns {string}
 */
export const sceneAssetUrl = (path, base = SCENE_BASE_URL) => {
  const root = normalizeSceneBase(base);
  if (!root || typeof path !== 'string') return path;
  if (path !== SCENE_PATH_PREFIX && !path.startsWith(`${SCENE_PATH_PREFIX}/`)) return path;
  return `${root}${path.slice(SCENE_PATH_PREFIX.length)}`;
};

const rewriteWall = (wall, base) => ({ ...wall, image: sceneAssetUrl(wall.image, base) });

/**
 * Copy of a registry entry with every scene URL (splat, window backdrop
 * image(s), walk grid, overlay JSON, mesh-room JSON) rewritten onto `base`.
 * A mesh-only entry (no `url`) stays without one. With no base the
 * entry itself is returned, so its identity is untouched.
 *
 * Call it once per entry at module load, never per render: SceneCanvas keys
 * its scene effect on the identity of `windowBackdrop`/`collision`.
 *
 * @param {object} scene
 * @param {string} [base]
 * @returns {object}
 */
export const withSceneAssetBase = (scene, base = SCENE_BASE_URL) => {
  if (!normalizeSceneBase(base)) return scene;
  const { windowBackdrop, collision } = scene;
  const resolved = { ...scene };
  if (scene.url) resolved.url = sceneAssetUrl(scene.url, base);
  if (windowBackdrop) {
    resolved.windowBackdrop = Array.isArray(windowBackdrop)
      ? windowBackdrop.map((wall) => rewriteWall(wall, base))
      : rewriteWall(windowBackdrop, base);
  }
  if (scene.overlays) resolved.overlays = sceneAssetUrl(scene.overlays, base);
  if (scene.meshRoom) resolved.meshRoom = sceneAssetUrl(scene.meshRoom, base);
  if (collision?.grid) resolved.collision = { ...collision, grid: sceneAssetUrl(collision.grid, base) };
  return resolved;
};
